import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileText, Plus, Search, Sparkles, Upload } from 'lucide-react';
import type {
  CursorPage, EmergencyCompany, EmergencyEmployeeMatch, EmergencySummaryFields, EmergencyVisitVenue,
} from '@dru/types';
import { EMERGENCY_BILL_STATUS_LABEL, EMERGENCY_SUMMARY_SOURCE_LABEL, formatPaise } from '@dru/types';
import {
  Alert, Button, Card, CardBody, CardHeader, ConfirmDialog, DataTable, ErrorState, Field, Input,
  Select, StatusBadge, Tabs, Textarea, TextSkeleton, useIdempotencyKey, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useAuth, errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { usePages } from '../../../lib/usePages';
import { formatDate, formatDateTime } from '../../../lib/format';
import { toBase64 } from '../../../lib/files';
import { Dialog } from '../../../components/Dialog';
import { PageHeader } from '../../../layouts/ConsoleShell';

/**
 * Emergency treatment at this venue, for a company that arranged it
 * (Requirement 2). The hospital identifies the employee by company + employee
 * code, types the doctor's notes as given, attaches the invoice and sends the
 * visit to Dr Ultimate. It may run the AI draft so Dr Ultimate starts from
 * it; Dr Ultimate confirms the summary, which submits the visit and bill to
 * the company for review — not approved and not paid — or returns it here.
 */

const today = () => new Date().toISOString().slice(0, 10);
type Tab = 'DRAFT' | 'AWAITING_CONFIRMATION' | 'SUBMITTED';
const BASE = '/venue/emergency';

export function EmergencyVisits() {
  const { permissions } = useAuth();
  const canRecord = permissions.has('emergency.record');
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('DRAFT');
  const [starting, setStarting] = useState(false);
  const page = usePages<EmergencyVisitVenue>(
    (cursor) => api.get<CursorPage<EmergencyVisitVenue>>(`${BASE}/visits?status=${tab}&limit=50${cursor ? `&cursor=${cursor}` : ''}`),
    [tab],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Emergency visits"
        hint="Employees of companies Dr Ultimate has arranged with your venue. Send each visit with its invoice to Dr Ultimate; once Dr Ultimate confirms the summary, the bill goes to the company for review — not approved or paid."
        actions={canRecord && <Button icon={<Plus className="h-4 w-4" />} onClick={() => setStarting(true)}>New emergency visit</Button>}
      />
      <Tabs
        tabs={[
          { key: 'DRAFT', label: 'Drafts' },
          { key: 'AWAITING_CONFIRMATION', label: 'With Dr Ultimate' },
          { key: 'SUBMITTED', label: 'Sent to company' },
        ]}
        active={tab}
        onChange={(k) => setTab(k as Tab)}
      />
      <DataTable<EmergencyVisitVenue>
        rows={page.items}
        loading={page.loading}
        error={page.error}
        onRetry={page.reload}
        hasMore={page.hasMore}
        onLoadMore={page.loadMore}
        loadingMore={page.loadingMore}
        rowKey={(v) => v.id}
        onRowClick={(v) => navigate(`/partner/emergency/${v.id}`)}
        empty={{
          title: tab === 'DRAFT' ? 'No drafts' : tab === 'AWAITING_CONFIRMATION' ? 'Nothing waiting with Dr Ultimate' : 'Nothing sent to a company yet',
          hint: tab === 'DRAFT' ? 'Start a visit when an employee of an arranged company arrives.'
            : tab === 'AWAITING_CONFIRMATION' ? 'Visits you send appear here until Dr Ultimate confirms the summary.'
              : 'Visits Dr Ultimate confirmed, and the company\'s review of each bill.',
          action: canRecord && tab === 'DRAFT' ? <Button onClick={() => setStarting(true)}>New emergency visit</Button> : undefined,
        }}
        columns={[
          { key: 'employee', header: 'Employee', cell: (v) => (
            <div><div className="font-medium">{v.employee.fullName}</div><div className="text-caption text-neutral-600">{v.employee.employeeCode}</div></div>
          ) },
          { key: 'company', header: 'Company', cell: (v) => v.company.name },
          { key: 'date', header: 'Treatment date', cell: (v) => formatDate(v.visitDate) },
          { key: 'amount', header: 'Billed', align: 'right', cell: (v) => (v.billedAmountPaise ? formatPaise(v.billedAmountPaise) : '—') },
          { key: 'state', header: 'Status', cell: (v) => (v.status === 'DRAFT'
            ? <StatusBadge tone={v.returnNote ? 'warning' : v.missing.length ? 'pending' : 'info'}>
              {v.returnNote ? 'Returned by Dr Ultimate' : v.missing.length ? `${v.missing.length} to do` : 'Ready to send'}
            </StatusBadge>
            : v.status === 'AWAITING_CONFIRMATION' ? <StatusBadge tone="info">With Dr Ultimate</StatusBadge>
            : <StatusBadge tone={v.billStatus === 'QUERIED' ? 'warning' : v.billStatus === 'REVIEWED' ? 'success' : 'neutral'}>
              {v.billStatus ? EMERGENCY_BILL_STATUS_LABEL[v.billStatus] : 'Submitted'}
            </StatusBadge>) },
        ]}
      />
      {starting && <StartVisitDialog onClose={() => setStarting(false)} onCreated={(id) => navigate(`/partner/emergency/${id}`)} />}
    </div>
  );
}

/** Company + employee code → the right person → a draft with the doctor's notes. */
function StartVisitDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const companies = useQuery(() => api.get<{ items: EmergencyCompany[] }>(`${BASE}/companies`), []);
  const [tenantId, setTenantId] = useState('');
  const [code, setCode] = useState('');
  const [match, setMatch] = useState<EmergencyEmployeeMatch | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [visitDate, setVisitDate] = useState(today());
  const [clinicianName, setClinicianName] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idem = useIdempotencyKey();
  const list = companies.data?.items ?? [];

  useEffect(() => { if (list.length === 1 && !tenantId) setTenantId(list[0]!.tenantId); }, [list, tenantId]);

  const find = async () => {
    setBusy(true); setError(null); setMatch(null); setConfirmed(false);
    try { setMatch(await api.post<EmergencyEmployeeMatch>(`${BASE}/lookup`, { tenantId, employeeCode: code.trim() })); }
    catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const create = async () => {
    if (!match) return;
    setBusy(true); setError(null);
    try {
      const v = await api.post<EmergencyVisitVenue>(`${BASE}/visits`, {
        tenantId: match.tenantId, employeeId: match.employeeId, visitDate, rawNotes: notes,
        ...(clinicianName.trim() ? { clinicianName: clinicianName.trim() } : {}),
      }, idem.key());
      onCreated(v.id);
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  };

  return (
    <Dialog title="New emergency visit" onClose={onClose} wide>
      <div className="space-y-4">
        {companies.error && <Alert>{companies.error}</Alert>}
        {!companies.loading && !list.length && !companies.error && (
          <Alert tone="info" title="No company has arranged emergency care here yet">
            Dr Ultimate arranges which companies your venue treats in an emergency. Until then no employee can be looked up.
          </Alert>
        )}
        {!confirmed && (
          <div className="grid gap-3 sm:grid-cols-[1fr_12rem_auto] sm:items-end">
            <Field label="Company" required>
              {(p) => (
                <Select {...p} value={tenantId} onChange={(e) => { setTenantId(e.target.value); setMatch(null); }}>
                  <option value="">Choose the company…</option>
                  {list.map((c) => <option key={c.tenantId} value={c.tenantId}>{c.name}</option>)}
                </Select>
              )}
            </Field>
            <Field label="Employee code" required hint="As printed on the employee's ID card.">
              {(p) => (
                <Input {...p} value={code} onChange={(e) => { setCode(e.target.value); setMatch(null); }}
                       onKeyDown={(e) => { if (e.key === 'Enter' && tenantId && code.trim()) void find(); }} />
              )}
            </Field>
            <Button variant="secondary" icon={<Search className="h-4 w-4" />} loading={busy && !match}
                    disabled={!tenantId || !code.trim()} onClick={find} className="max-md:w-full">Find</Button>
          </div>
        )}
        {error && <Alert>{error}</Alert>}
        {match && (
          <div className="flex items-center gap-4 rounded-card border border-neutral-200 p-3">
            {match.photo
              ? <img src={match.photo.url} alt="" className="h-16 w-16 shrink-0 rounded-full object-cover" />
              : <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-h3 text-neutral-500">{match.fullName.slice(0, 1)}</div>}
            <div className="min-w-0 flex-1 text-small">
              <div className="text-body font-semibold text-neutral-900">{match.fullName}</div>
              <div className="text-neutral-700">{match.employeeCode} · {match.company}</div>
              <div className="text-neutral-600">
                {[match.sex && match.sex.charAt(0) + match.sex.slice(1).toLowerCase(), match.birthYear && `born ${match.birthYear}`, match.plant].filter(Boolean).join(' · ')}
              </div>
            </div>
            {!confirmed && <Button onClick={() => setConfirmed(true)}>This is them</Button>}
          </div>
        )}
        {confirmed && match && (
          <div className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Treatment date" required>
                {(p) => <Input {...p} type="date" max={today()} value={visitDate} onChange={(e) => setVisitDate(e.target.value)} />}
              </Field>
              <Field label="Treating doctor">
                {(p) => <Input {...p} value={clinicianName} onChange={(e) => setClinicianName(e.target.value)} placeholder="Dr …" />}
              </Field>
            </div>
            <Field label="Doctor's notes" required hint="As the doctor gave them — abbreviations and Hindi/Hinglish are fine. They are kept exactly as typed.">
              {(p) => <Textarea {...p} rows={6} value={notes} onChange={(e) => setNotes(e.target.value)}
                                placeholder="Presenting issue, examination findings, treatment given" />}
            </Field>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
              <Button loading={busy} disabled={notes.trim().length < 3 || !visitDate} onClick={create}>Save draft</Button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export function EmergencyVisitDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const canRecord = permissions.has('emergency.record');
  const { toast } = useToast();
  const q = useQuery(() => api.get<EmergencyVisitVenue>(`${BASE}/visits/${id}`), [id]);
  const [v, setV] = useState<EmergencyVisitVenue | null>(null);
  useEffect(() => { if (q.data) setV(q.data); }, [q.data]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const sendKey = useIdempotencyKey();

  const run = async (what: string, fn: () => Promise<EmergencyVisitVenue>, done?: string) => {
    setBusy(what); setError(null);
    try { const next = await fn(); setV(next); if (done) toast('success', done); return true; }
    catch (e) { setError(errorMessage(e)); return false; }
    finally { setBusy(null); }
  };

  if (q.error) return <ErrorState title="This visit could not be loaded" detail={q.error} onRetry={q.reload} />;
  if (!v) return <TextSkeleton lines={8} />;
  const draft = v.status === 'DRAFT';

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${v.employee.fullName} · ${v.employee.employeeCode}`}
        hint={`${v.company.name} · treated ${formatDate(v.visitDate)} at ${v.partner.name}`}
        actions={draft && canRecord && (
          <Button variant="ghost" onClick={() => setDiscarding(true)}>Discard draft</Button>
        )}
      />
      {draft && v.returnNote && (
        <Alert tone="warning" title="Returned by Dr Ultimate">“{v.returnNote}” Correct the notes or invoice and send it again.</Alert>
      )}
      {v.status === 'AWAITING_CONFIRMATION' && (
        <Alert tone="info" title="With Dr Ultimate">
          Sent {formatDateTime(v.sentAt!)}. Dr Ultimate will confirm the summary, which sends the visit and bill to {v.company.name}, or return it to you.
        </Alert>
      )}
      {v.status === 'SUBMITTED' && (
        <Alert tone="success" title="Sent to the company">
          Confirmed {formatDateTime(v.submittedAt!)}. The bill is {v.billStatus ? EMERGENCY_BILL_STATUS_LABEL[v.billStatus].toLowerCase() : 'awaiting review'} — this does not approve or pay it.
          {v.reviewNote && <> Company note: “{v.reviewNote}”.</>}
        </Alert>
      )}
      {error && <Alert>{error}</Alert>}

      <NotesCard v={v} editable={draft && canRecord} busy={busy === 'notes'}
                 onSave={(patch) => run('notes', () => api.patch(`${BASE}/visits/${id}`, patch), 'Saved')} />

      <SummaryCard v={v} draft={draft && canRecord} busy={busy === 'ai'}
                   onStructure={() => run('ai', () => api.post(`${BASE}/visits/${id}/structure`))} />

      <InvoiceCard v={v} editable={draft && canRecord} busy={busy}
                   onSave={(patch) => run('bill', () => api.patch(`${BASE}/visits/${id}`, patch), 'Saved')}
                   onUpload={(file) => run('upload', async () => api.post(`${BASE}/visits/${id}/invoice`, {
                     fileName: file.name, mimeType: file.type, content: await toBase64(file),
                   }), 'Invoice attached')}
                   onOpen={async () => {
                     try { const g = await api.get<{ url: string }>(`${BASE}/visits/${id}/invoice`); window.open(`${g.url}?inline=1`, '_blank', 'noopener'); }
                     catch (e) { setError(errorMessage(e)); }
                   }} />

      {draft && canRecord && (
        <Card>
          <CardHeader title="Send to Dr Ultimate" />
          <CardBody className="space-y-3">
            {v.missing.length ? (
              <ul className="space-y-1 text-small text-neutral-700">
                {v.missing.map((m) => <li key={m} className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-600" />{m}</li>)}
              </ul>
            ) : (
              <p className="flex items-center gap-2 text-small text-neutral-700"><CheckCircle2 className="h-4 w-4 text-success-600" />Ready. Once sent, the notes and invoice cannot be changed unless Dr Ultimate returns the visit.</p>
            )}
            <Button disabled={v.missing.length > 0} loading={busy === 'send'} className="max-md:w-full"
                    onClick={async () => { if (await run('send', () => api.post(`${BASE}/visits/${id}/send`, {}, sendKey.key()), 'Sent to Dr Ultimate')) sendKey.rotate(); }}>
              Send to Dr Ultimate
            </Button>
          </CardBody>
        </Card>
      )}

      {discarding && (
        <ConfirmDialog
          title="Discard this draft?"
          what="The draft is removed from your list. Nothing has been sent to the company."
          confirmLabel="Discard"
          tone="danger"
          onCancel={() => setDiscarding(false)}
          onConfirm={async () => {
            try { await api.del(`${BASE}/visits/${id}`); navigate('/partner/emergency'); }
            catch (e) { setError(errorMessage(e)); setDiscarding(false); }
          }}
        />
      )}
    </div>
  );
}

function NotesCard({ v, editable, busy, onSave }: {
  v: EmergencyVisitVenue; editable: boolean; busy: boolean;
  onSave: (patch: Record<string, unknown>) => Promise<boolean>;
}) {
  const [notes, setNotes] = useState(v.rawNotes);
  const [date, setDate] = useState(v.visitDate);
  const [doctor, setDoctor] = useState(v.clinicianName ?? '');
  useEffect(() => { setNotes(v.rawNotes); setDate(v.visitDate); setDoctor(v.clinicianName ?? ''); }, [v.rawNotes, v.visitDate, v.clinicianName]);
  const dirty = notes.trim() !== v.rawNotes || date !== v.visitDate || doctor.trim() !== (v.clinicianName ?? '');
  return (
    <Card>
      <CardHeader title="Doctor's original notes" hint="Kept exactly as written, and read by Dr Ultimate when confirming the summary." />
      <CardBody className="space-y-3">
        {editable ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Treatment date" required>{(p) => <Input {...p} type="date" max={today()} value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
              <Field label="Treating doctor">{(p) => <Input {...p} value={doctor} onChange={(e) => setDoctor(e.target.value)} />}</Field>
            </div>
            <Field label="Notes" required>{(p) => <Textarea {...p} rows={6} value={notes} onChange={(e) => setNotes(e.target.value)} />}</Field>
            {dirty && (
              <Button variant="secondary" loading={busy} disabled={notes.trim().length < 3}
                      onClick={() => onSave({ rawNotes: notes, visitDate: date, clinicianName: doctor.trim() || null })}>Save notes</Button>
            )}
          </>
        ) : (
          <>
            {v.clinicianName && <p className="text-small text-neutral-600">Treating doctor: {v.clinicianName}</p>}
            <p className="whitespace-pre-wrap rounded-input bg-neutral-50 p-3 text-body text-neutral-900">{v.rawNotes}</p>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * The AI draft and, once confirmed, the summary. The venue may run the draft
 * so Dr Ultimate starts from it; confirming is Dr Ultimate's.
 */
function SummaryCard({ v, draft, busy, onStructure }: {
  v: EmergencyVisitVenue; draft: boolean; busy: boolean; onStructure: () => Promise<boolean>;
}) {
  const shown = v.summaryConfirmedAt ? v : v.aiDraft;
  const fields: Array<[keyof EmergencySummaryFields, string]> = [
    ['presentingIssue', 'Presenting issue'], ['examinationFindings', 'Examination findings'], ['treatmentGiven', 'Treatment given'],
  ];
  return (
    <Card>
      <CardHeader
        title={v.summaryConfirmedAt ? 'Summary confirmed by Dr Ultimate' : 'AI draft of the summary'}
        hint="The AI only restructures and translates the notes; it adds nothing. Dr Ultimate reviews the notes and confirms the summary."
        actions={draft && (
          <Button variant="secondary" size="sm" icon={<Sparkles className="h-4 w-4" />} loading={busy} onClick={onStructure}>
            {v.aiDraft ? 'Draft again with AI' : 'Draft with AI'}
          </Button>
        )}
      />
      <CardBody className="space-y-3">
        {!shown ? (
          <p className="text-small text-neutral-600">No draft yet. This is optional — Dr Ultimate can draft or write the summary from your notes.</p>
        ) : (
          <>
            {!v.summaryConfirmedAt && v.aiDraft && (
              <p className="text-caption text-neutral-600">
                AI draft by {v.aiModel} · {v.aiGeneratedAt ? formatDateTime(v.aiGeneratedAt) : ''}
                {v.aiStale && <span className="text-warning-700"> · the notes changed since</span>}
              </p>
            )}
            {fields.map(([k, label]) => (
              <div key={k}>
                <div className="text-caption font-medium text-neutral-600">{label}</div>
                <p className="whitespace-pre-wrap text-body text-neutral-900">{shown[k] || '—'}</p>
              </div>
            ))}
            {!v.summaryConfirmedAt && v.aiDraft && v.aiDraft.ambiguities.length > 0 && (
              <Alert tone="warning" title="Unclear in the notes">
                <ul className="list-disc pl-4">{v.aiDraft.ambiguities.map((a) => <li key={a}>{a}</li>)}</ul>
              </Alert>
            )}
            {v.summaryConfirmedAt && (
              <p className="flex items-center gap-2 text-small text-neutral-700">
                <CheckCircle2 className="h-4 w-4 text-success-600" />
                Confirmed {formatDateTime(v.summaryConfirmedAt)}{v.summarySource && ` · ${EMERGENCY_SUMMARY_SOURCE_LABEL[v.summarySource]}`}
              </p>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

function InvoiceCard({ v, editable, busy, onSave, onUpload, onOpen }: {
  v: EmergencyVisitVenue; editable: boolean; busy: string | null;
  onSave: (patch: Record<string, unknown>) => Promise<boolean>;
  onUpload: (file: File) => Promise<boolean>;
  onOpen: () => void;
}) {
  const [reason, setReason] = useState(v.billingReason ?? '');
  const [number, setNumber] = useState(v.invoiceNumber ?? '');
  const [amount, setAmount] = useState(v.billedAmountPaise ? String(v.billedAmountPaise / 100) : '');
  const paise = amount.trim() ? Math.round(Number(amount) * 100) : null;
  const amountValid = paise === null || (Number.isFinite(paise) && paise > 0);
  const dirty = reason.trim() !== (v.billingReason ?? '') || number.trim() !== (v.invoiceNumber ?? '') || paise !== v.billedAmountPaise;

  return (
    <Card>
      <CardHeader title="Invoice" hint="Shown to the company's billing reviewers. Keep the reason in billing words — not a diagnosis." />
      <CardBody className="space-y-3">
        {editable ? (
          <>
            <Field label="Reason for billing" required>
              {(p) => <Input {...p} value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Emergency consultation and dressing" />}
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Invoice number" required>{(p) => <Input {...p} value={number} onChange={(e) => setNumber(e.target.value)} />}</Field>
              <Field label="Total billed (₹)" required error={amountValid ? null : 'Enter an amount above zero'}>
                {(p) => <Input {...p} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />}
              </Field>
            </div>
            {dirty && (
              <Button variant="secondary" loading={busy === 'bill'} disabled={!amountValid}
                      onClick={() => onSave({ billingReason: reason.trim() || null, invoiceNumber: number.trim() || null, billedAmountPaise: paise })}>
                Save invoice details
              </Button>
            )}
          </>
        ) : (
          <dl className="grid gap-2 text-small sm:grid-cols-3">
            <div><dt className="text-neutral-600">Reason</dt><dd className="text-neutral-900">{v.billingReason ?? '—'}</dd></div>
            <div><dt className="text-neutral-600">Invoice number</dt><dd className="text-neutral-900">{v.invoiceNumber ?? '—'}</dd></div>
            <div><dt className="text-neutral-600">Total billed</dt><dd className="text-neutral-900">{v.billedAmountPaise ? formatPaise(v.billedAmountPaise) : '—'}</dd></div>
          </dl>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {v.invoice ? (
            <button type="button" onClick={onOpen} className="inline-flex items-center gap-2 text-small font-medium text-primary-700 hover:underline max-md:min-h-10">
              <FileText className="h-4 w-4" />{v.invoice.fileName}
            </button>
          ) : <span className="text-small text-neutral-600">No invoice file attached.</span>}
          {editable && (
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-input border border-neutral-300 px-3 py-1.5 text-small font-medium text-neutral-800 hover:bg-neutral-50 max-md:min-h-10">
              <Upload className="h-4 w-4" />{busy === 'upload' ? 'Uploading…' : v.invoice ? 'Replace file' : 'Attach PDF or image'}
              <input type="file" accept="application/pdf,image/jpeg,image/png" className="sr-only"
                     onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ''; if (file) void onUpload(file); }} />
            </label>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
